/**
 * Anything that relates to handling entitlement body payloads, including transforming
 * or absorbing them into state, should live here.
 */

import * as z from 'zod';

import { aPersonCanInteractWithPaywalls } from '@canvas/capability-summaries';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';
import { safeGet, safeSet } from '@canvas/idb-cache';
import {
  PaywallFeature,
  type EntitlementFeatureBody,
  type PaywallFeatureStateFromEntitlement,
} from '@canvas/paywall-types';
import {
  CustomerImpact,
  EventClassification,
  addBreadcrumb,
  captureMessageForCapability,
} from '@canvas/sentry-react';

import { getCTAConfigForFeature } from './paywall-cta-config';
import {
  currentPaywallFeatureState,
  RELEASED_PAYWALL_FEATURES,
  ALL_FEATURES,
} from './paywall-features-state';

const featureSchema = z
  .object({
    isEntitled: z.boolean(),
  })
  .required({
    isEntitled: true,
  })
  .passthrough();

export const getEntitlementFeaturesValidator = (
  required: ReadonlyArray<PaywallFeature> = RELEASED_PAYWALL_FEATURES
) => {
  const expectedEntitlementFeatureSchema = z
    .object({
      whiteboards: z
        .object(
          Object.fromEntries(
            Object.values(PaywallFeature).map((paywallFeature) => {
              // Note: You could make this a set to get O(1) speeds at the
              // current # of features we have, memory & GC likely outweighs the tradeoff.
              const maybeOptionalFeature = required.includes(paywallFeature)
                ? featureSchema
                : featureSchema.optional();

              return [paywallFeature, maybeOptionalFeature];
            })
          )
        )
        .passthrough(),
    })
    .required({
      whiteboards: true,
    })
    .passthrough();

  return expectedEntitlementFeatureSchema;
};

const entitlementFeaturesSchema = getEntitlementFeaturesValidator();

export const validateEntitlementAndTransformToPaywallState = (
  entitlementFeatures: EntitlementFeatureBody | undefined
): PaywallFeatureStateFromEntitlement | undefined => {
  const validationResult = entitlementFeaturesSchema.safeParse(entitlementFeatures);
  if (!validationResult.success) {
    const errorMessage =
      'Entitlement response is missing data that was expected to be present by the paywall feature schema';

    ReliabilityMetrics.getInstance().failure(
      MetricName.PAYWALL_FEATURE_SCHEMA_VALIDATION,
      { errorMessage }
    );

    captureMessageForCapability(
      errorMessage,
      {
        contexts: {
          'Entitlement response': entitlementFeatures,
          'Paywall feature schema validation errors': {
            ...validationResult.error,
          },
        },
        tags: {
          captureLocation: 'validateEntitlementAndTransformToPaywall-invalid-schema',
          customerImpact: CustomerImpact.CUSTOMERS,
          eventClassification: EventClassification.BUG,
        },
        level: 'error',
      },
      /**
       * Since the schema is corrupted, we might not see the paywall at all (at the time of writing we allow the feature when we can't find the right data)
       */
      aPersonCanInteractWithPaywalls
    );
  }

  return entitlementFeatures?.whiteboards;
};

/**
 * Makes sure the CTA image is shown instantly upon being trigerred.
 * Note that you should avoid trigerring unless you know for a fact that the user is not
 * allowed to use a given feature - Otherwise the image will never actually be seen.
 */
const preloadFeatureCtaImages = (feature: PaywallFeature) => {
  const ctaConfig = getCTAConfigForFeature(feature);
  if (!ctaConfig) {
    return;
  }
  const imageSrc = ctaConfig.image.src;

  new Image().src = imageSrc.dark;
  new Image().src = imageSrc.default;
};

export const updatePaywallFeatureState = (
  updatedState: PaywallFeatureStateFromEntitlement
) => {
  for (const uncastFeatureKey of Object.keys(updatedState)) {
    if (!ALL_FEATURES.includes(uncastFeatureKey as PaywallFeature)) {
      continue;
    }

    const featureKey = uncastFeatureKey as PaywallFeature;

    currentPaywallFeatureState[featureKey] = {
      ...currentPaywallFeatureState[featureKey],
      ...updatedState[featureKey],
    };

    if (!currentPaywallFeatureState[featureKey].isEntitled) {
      preloadFeatureCtaImages(featureKey);
    }
  }

  addBreadcrumb({
    type: 'info',
    level: 'info',
    message: 'Updated the current paywall feature state',
    data: currentPaywallFeatureState,
  });
};

export const WHITEBOARD_PAYWALL_IDB_CACHE_KEY = 'whiteboard-paywall-feature-data';

export const setPaywallCacheFeatureData = (
  data: PaywallFeatureStateFromEntitlement
): Promise<void> => {
  return safeSet(WHITEBOARD_PAYWALL_IDB_CACHE_KEY, data);
};

export const getPaywallCacheFeatureData = (): Promise<
  PaywallFeatureStateFromEntitlement | undefined
> => {
  return safeGet(WHITEBOARD_PAYWALL_IDB_CACHE_KEY);
};
